import { TCards } from '@lbs/shared-rp-types'
import { animate, motion, useMotionValue } from 'framer-motion'
import { FC, useEffect, useRef, useState } from 'react'
import { StyledDetailCard, StyledExtraActionCard } from '../CardHolder.styles'
import { CardInteractionVM, ExtraActionVM } from '../CardHolder.types'
import { ReactComponent as OneMoreIcon } from '../img/oneMore.svg'
import { ReactComponent as SwitchIcon } from '../img/switch.svg'
import { ReactComponent as RejectBlockedIcon } from '../../Icons/img/reject.svg'
import { useCardHoverFlip } from './useCardHoverFlip'

// ─── Timing constants (exported for synchronization with DecisionBox) ─────────

const FLIP_INITIAL_DELAY_S = 0.15 // wait for box layout animation to finish
const FLIP_HALF_S = 0.1 // duration of each half-phase (fold or reveal)
const FLIP_STAGGER_S = 0.2 // delay between consecutive cards

// Total time until the first card (index 0) finishes revealing:
// initialDelay + (n-1) * stagger + fullFlip
// For 5 cards: 0.15 + 4 * 0.2 + 0.2 = 1.15s → round up with buffer
export const CARD_FLIP_TOTAL_MS = 1250

// ─── FlipCard ─────────────────────────────────────────────────────────────────
// Two-phase 2D horizontal flip:
//   Phase 1: scaleX 1 → 0  (card folds to edge, showing face-down XX)
//   Midpoint: swap to real card value
//   Phase 2: scaleX 0 → 1  (card unfolds, revealing face-up)

type FlipCardProps = {
  card: TCards
  delay: number
  cardRef: (el: HTMLDivElement | null) => void
  darkened: boolean
  isClickable: boolean
  onClick?: () => void
  onMouseEnter: () => void
  // Called once after Phase 2 (scaleX 0→1) completes. Only the last-revealing card receives this.
  onRevealComplete?: () => void
}

const FlipCard: FC<FlipCardProps> = ({
  card,
  delay,
  cardRef,
  darkened,
  isClickable,
  onClick,
  onMouseEnter,
  onRevealComplete,
}) => {
  const [visibleCard, setVisibleCard] = useState<TCards>('XX' as TCards)
  const scaleX = useMotionValue(1)
  const didNotify = useRef(false)

  useEffect(() => {
    // Skip animation for placeholder cards (XX). The server sends decisions
    // before real card values arrive, so DetailCardsView may mount with XX cards
    // first. When real cards arrive the key changes (XX-N → Ah-N) and FlipCard
    // remounts — that remount triggers the animation correctly.
    if (card === ('XX' as TCards)) return undefined

    let isMounted = true

    const fold = animate(scaleX, 0, {
      duration: FLIP_HALF_S,
      delay,
      ease: 'easeIn',
    })

    fold.then(() => {
      if (!isMounted) return
      setVisibleCard(card)
      animate(scaleX, 1, { duration: FLIP_HALF_S, ease: 'easeOut' }).then(
        () => {
          if (!isMounted || didNotify.current) return
          didNotify.current = true
          onRevealComplete?.()
        }
      )
    })

    return () => {
      isMounted = false
      fold.stop()
    }
    // Runs only on mount — key prop handles remount when card changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <motion.div style={{ scaleX, display: 'flex' }}>
      <StyledDetailCard
        ref={cardRef}
        value={visibleCard}
        darkened={darkened}
        isClickable={isClickable}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
      />
    </motion.div>
  )
}

// ─── DetailCardsView ──────────────────────────────────────────────────────────

type Props = {
  cards: TCards[]
  enableHoverFlip: boolean
  cardInteraction?: CardInteractionVM | null
  extraAction?: ExtraActionVM | null
  onRevealComplete?: () => void
}

const DetailCardsView: FC<Props> = ({
  cards,
  enableHoverFlip,
  cardInteraction = null,
  extraAction = null,
  onRevealComplete,
}) => {
  const { cardRefs, handleCardHover } = useCardHoverFlip(enableHoverFlip)

  return (
    <>
      {extraAction && (
        <motion.div style={{ display: 'flex' }}>
          <StyledExtraActionCard
            value={null}
            variant={extraAction.variant}
            isActive={extraAction.isActive}
            isEnabled={extraAction.isEnabled}
            onClick={extraAction.isEnabled ? extraAction.onAction : undefined}>
            <SwitchIcon
              className={
                extraAction.isEnabled && extraAction.variant === 'switchCards'
                  ? 'active'
                  : ''
              }
            />
            <OneMoreIcon
              className={
                extraAction.isEnabled && extraAction.variant === 'buySixCard'
                  ? 'active'
                  : ''
              }
            />
            <RejectBlockedIcon
              className={!extraAction.isEnabled ? 'active blocked' : 'blocked'}
            />
          </StyledExtraActionCard>
        </motion.div>
      )}
      {cards.map((card, idx) => (
        <FlipCard
          // eslint-disable-next-line react/no-array-index-key
          key={`${card}-${idx}`}
          card={card}
          // Last card flips first, first card flips last — idx 0 finishes last.
          delay={
            FLIP_INITIAL_DELAY_S + (cards.length - 1 - idx) * FLIP_STAGGER_S
          }
          cardRef={(el) => {
            cardRefs.current[idx] = el
          }}
          darkened={
            cardInteraction ? cardInteraction.darkenedCards.has(card) : false
          }
          isClickable={cardInteraction?.isClickable ?? false}
          onClick={
            cardInteraction ? () => cardInteraction.onClick(card) : undefined
          }
          onMouseEnter={() => handleCardHover(idx)}
          onRevealComplete={idx === 0 ? onRevealComplete : undefined}
        />
      ))}
    </>
  )
}

export default DetailCardsView
